import React, { useEffect, useState } from "react";
import { BellOff, ChevronsUpDown, EllipsisVertical, History, MessageSquareText, Trash2 } from "lucide-react";
import { can_visit, usPhoneFormat } from "../../helpers";
import DeleteModal from "../Modal/DeleteModal";
import CustomBulkMessageSendModal from "../Modal/CustomBulkMessageSendModal";
import FidelisPatientHistoryModal from "../Modal/FidelisPatientHistoryModal";
import Skeleton from "react-loading-skeleton";
const maleAvatar = './images/avatar.png';
const femaleAvatar = './images/female.png';
const otherAvatar = './images/other.png';

const styles = {
  display: "flex",
  alignItems: "center",
  gap: "5px",
  cursor: "pointer",
};

export default function FidelisPatientDataTable(props) {
  const {
    account_id,
    location_id,
    tableData = [],
    handleCheckboxChange = () => {},
    handleSelectAll = () => {},
    handleDeleteRecord = () => {},
    reloadData = () => {},
    reloadDataFilter = {},
    dataLoading,
    setDataLoading = () => {},
    checkSelected = false,
    checkedIds = [],
    setCheckedIds = () => {},
    globalCheckedIds,
    setGlobalCheckedIds = () => {},
    previousScrollPosition = 0,
    setPreviousScrollPosition = () => {},
    smsLimit,
    setSmsLimit = () => {},
    selectedPatientData,
    setSelectedPatientData = () => {},
    fidelisPatientBulkDelete = false,
    setFidelisPatientBulkDelete = () => {},
    messageTemplate = [],
  } = props;

  const [orderBy, setOrderBy] = useState();
  const [selectedPatient, setSelectedPatient] = useState({});
  const [activeDeleteModal, setActiveDeleteModal] = useState(false);
  const [activeMessageModal, setActiveMessageModal] = useState(false);
  const [viewStatusOptions, setViewStatusOptions] = useState({});
  const [activeHistoryModal, setActiveHistoryModal] = useState(false);

  const handleOrderChange = (name, type = "asc") => {
    name = name.toString();
    setOrderBy(name);
    if (reloadDataFilter.order_by && reloadDataFilter.order_by[0] == name) {
      if (reloadDataFilter.order_by[1] == "asc") {
        type = "desc";
      }
    }
    const orderFilter = { order_by: [name, type.toString()] };
    const filter_merge = { ...reloadDataFilter, ...orderFilter };
    reloadData(filter_merge);
    setDataLoading(true);
  };

  const removeSelection = () => {
    setCheckedIds([]);
    setGlobalCheckedIds(new Set());
  };

  const handleSendSingleMessage = (item) => {
    setSelectedPatient(item);
    setActiveMessageModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  }

  const handleDelete = (item) => {
    setSelectedPatient(item);
    setActiveDeleteModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  }
  
  const handleViewHistory = (item) => {
    setSelectedPatient(item);
    setActiveHistoryModal(true);
    setPreviousScrollPosition(window.scrollY);
    window.scrollTo(0, 0);
  }

  const buttonArray = [
    { label: 'View history', icon: History, isButton: true, action: handleViewHistory },
    { label: 'Send message', icon: MessageSquareText, isButton: true, action: handleSendSingleMessage },
    { label: 'Delete', icon: Trash2, isButton: true, action: handleDelete },
  ];
  const permissions = {
    showButton1: can_visit('fidelis_care'),
    showButton2: can_visit('_single_message'),
    showButton3: can_visit('fidelis_care_delete'),
  };

  const handleOutsideClick = (event) => {
    if (!event.target.closest(".dropdown-container")) {
      setViewStatusOptions({});
    }
  };

  useEffect(() => {
    document.addEventListener("click", handleOutsideClick);
    return () => {
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const handleDropdownClick = (index) => {
    setViewStatusOptions((prev) => {
      const newViewStatusOptions = {};
      newViewStatusOptions[index] = !prev[index];
      return newViewStatusOptions;
    });
  };

  return (
    <>
      {checkSelected && (
        <>
          <hr />
          <div className="docpro_data_table_btns">
            {can_visit('_bulk_message') && (
              <>
                <CustomBulkMessageSendModal
                  account_id={account_id}
                  location_id={location_id}
                  smsLimit={smsLimit}
                  setSmsLimit={setSmsLimit}
                  checkedIds={Array.from(globalCheckedIds)}
                  setGlobalCheckedIds={setGlobalCheckedIds}
                  selectedPatientData={selectedPatientData}
                  setSelectedPatientData={setSelectedPatientData}
                  setCheckedIds={setCheckedIds}
                  messageTemplate={messageTemplate}
                />
              </>
            )}

            {can_visit('fidelis_care_delete') && (
              <button className="docpro_bordered_btn docpro_fw_600 docpro_red_color"
                onClick={() => {
                  setFidelisPatientBulkDelete(!fidelisPatientBulkDelete);
                  setPreviousScrollPosition(window.scrollY);
                  window.scrollTo(0, 0);
                }}
              >
                <Trash2 width={20} height={20} />
                Delete
              </button>
            )}

            {checkedIds?.length > 0 && (
              <div className='docpro_filtered_badge' style={{ margin: 0, borderRadius: '8px' }}>
                <span><b>Selected Patients</b>: {checkedIds?.length}</span>
                <button
                  onClick={removeSelection}
                >
                  ✕
                </button>
              </div>
            )}
          </div>
        </>
      )}
      <div className="docpro_table_responsive" style={{ minHeight: "450px" }}>
        <table className="docpro_mb_0" style={{ height: '100%' }}>
          <thead>
            <tr>
              <th style={{ width: "45px" }}>
                <div className="docpro_checkbox" style={{ top: "3px" }}>
                  <input
                    type="checkbox"
                    checked={tableData.length > 0 && tableData.every((row) => globalCheckedIds?.has(row?.id))}
                    onChange={handleSelectAll}
                    disabled={tableData.length === 0}
                  />
                  <label></label>
                </div>
              </th>
              <th
                onClick={() => {
                  handleOrderChange("patient_name");
                }}
                className={`${orderBy === "patient_name" ? "docpro_accent_color" : ""}`}
              >
                <div style={styles}>
                  Patient Name <ChevronsUpDown size={"12"} />
                </div>
              </th>
              <th
                onClick={() => {
                  handleOrderChange("practice_location");
                }}
                className={`${orderBy === "practice_location" ? "docpro_accent_color" : ""}`}
              >
                <div style={styles}>
                  Location <ChevronsUpDown size={"12"} />
                </div>
              </th>
              <th>Address</th>
              <th
                onClick={() => {
                  handleOrderChange("phone");
                }}
                className={`${orderBy === "phone" ? "docpro_accent_color" : ""}`}
              >
                <div style={styles}>
                  Phone <ChevronsUpDown size={"12"} />
                </div>
              </th>
              <th
                onClick={() => {
                  handleOrderChange("gender");
                }}
                className={`${orderBy === "gender" ? "docpro_accent_color" : ""}`}
              >
                <div style={styles}>
                  Gender <ChevronsUpDown size={"12"} />
                </div>
              </th>
              <th
                onClick={() => {
                  handleOrderChange("visit_status");
                }}
                className={`${orderBy === "visit_status" ? "docpro_accent_color" : ""}`}
              >
                <div style={styles}>
                  Visit Status <ChevronsUpDown size={"12"} />
                </div>
              </th>
              <th className="text-right"><span className="pr-2"></span></th>
            </tr>
          </thead>
          <tbody>
            {!dataLoading ? (
              tableData.length > 0 ? (
                tableData?.map((item, index) => (
                  <tr key={index} className={`row_${item.id}`}>
                    <td style={{ width: "45px" }}>
                      <div className="docpro_checkbox" style={{ top: "3px" }}>
                        <input
                          type="checkbox"
                          checked={globalCheckedIds.has(item.id)}
                          onChange={() => handleCheckboxChange(item.id, item)}
                        />
                        <label></label>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center table_img">
                        <img src={item?.gender === "Male" ? maleAvatar : item?.gender === "Female" ? femaleAvatar : otherAvatar} alt="User" />
                        {item?.patient_name}
                        {!(item?.is_active) && (
                          <span className="docpro_icon_btn overview" style={{ width: 'auto', height: 'auto', borderRadius: '50%', marginLeft: '5px' }} >
                            <BellOff width={16} height={16} class="docpro_red_color" />
                            <div className="docpro_tip" style={{ textTransform: 'unset' }}>
                              <span>Messaging stopped by patient request</span>
                            </div>
                          </span>
                        )}
                      </div>
                    </td>
                    <td> {item?.practice_location} </td>
                    <td> {item?.address ? `${item?.address} ` : ""} {item?.city ? `${item?.city} ` : ""}{item?.state ? `${item?.state} ` : ""}{item?.zip ? item?.zip : ""}</td>
                    <td> {item?.phone ? usPhoneFormat(item?.phone) : ""} </td>
                    <td> {item?.gender} </td>
                    <td> {item?.visit_status} </td>
                    <td className="docpro_text_center">
                      <div className="docpro_icon_btns items-center" style={{ justifyContent: 'end' }}>
                        {buttonArray
                          .filter((_, index1) => permissions[`showButton${index1 + 1}`])
                          .slice(0, 2)
                          .map((btn, btnIndex) => (
                            <div key={btnIndex}>
                              <button type="button" className="docpro_icon_btn" onClick={() => btn.action(item)}>
                                <btn.icon width={18} height={18} />
                                <span className="docpro_tip">{btn.label}</span>
                              </button>
                            </div>
                          ))
                        }
                        {buttonArray?.length > 2 &&
                          buttonArray.filter((_, index2) => permissions[`showButton${index2 + 1}`]).length > 2 && (
                            <div className="dropdown-container relative">
                              <button className="docpro_icon_btn" onClick={() => handleDropdownClick(index)}>
                                <EllipsisVertical width={18} height={18} />
                              </button>
                              {viewStatusOptions[index] && (
                                <div className={`docpro_button_toggle_options ${index > 4 && 'top_position'}`}>
                                  {buttonArray
                                    .filter((_, index2) => permissions[`showButton${index2 + 1}`])
                                    .slice(2)
                                    .map((btn, btnIndex) => (
                                      <div key={btnIndex}>
                                        <button
                                          type="button"
                                          className="docpro_icon_btn docpro_dropdown_button"
                                          onClick={() => btn.action(item)}
                                        >
                                          <btn.icon width={18} height={18} />
                                          <span>{btn.label}</span>
                                        </button>
                                      </div>
                                    ))}
                                </div>
                              )}
                            </div>
                          )
                        }
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="no_data docpro_p_0" style={{ height: "450px" }} colSpan="8">
                    No data available
                  </td>
                </tr>
              )
            ) : (
              Array.from({ length: 8 }).map((_, index) => (
                <tr key={index} style={{ height: '50px' }}>
                  <td style={{ width: "45px" }}> <Skeleton width="1.2rem" borderRadius="8px"></Skeleton> </td>
                  <td> <Skeleton width={`${5 + (index % 2.5)}rem`} borderRadius="8px"></Skeleton> </td>
                  <td> <Skeleton width={`${4 + (index % 1.2)}rem`} borderRadius="8px"></Skeleton> </td>
                  <td> <Skeleton width={`${4 + (index % 3)}rem`} borderRadius="8px"></Skeleton> </td>
                  <td> <Skeleton width="6rem" borderRadius="8px"></Skeleton> </td>
                  <td> <Skeleton width={`${2 + (index % 1.5)}rem`} borderRadius="8px"></Skeleton> </td>
                  <td> <Skeleton width={`${4 + (index % 2)}rem`} borderRadius="8px"></Skeleton> </td>
                  <td className="text-right"> <span className="mr-4 pr-2"><Skeleton width="3rem" borderRadius="8px"></Skeleton></span> </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {activeDeleteModal && (
        <DeleteModal
          handleDelete={() => handleDeleteRecord(selectedPatient?.id)}
          name={selectedPatient?.patient_name}
          activeModal={activeDeleteModal}
          setActiveModal={setActiveDeleteModal}
          previousScrollPosition={previousScrollPosition}
          setPreviousScrollPosition={setPreviousScrollPosition}
        />
      )}

      {activeHistoryModal && (
        <FidelisPatientHistoryModal
          account_id={account_id}
          location_id={location_id}
          emr_patient_id={selectedPatient?.emr_patient_id}
          activeModal={activeHistoryModal}
          setActiveModal={setActiveHistoryModal}
          previousScrollPosition={previousScrollPosition}
        />
      )}

      {activeMessageModal && (
        <CustomBulkMessageSendModal
          account_id={account_id}
          location_id={location_id}
          smsLimit={smsLimit}
          setSmsLimit={setSmsLimit}
          setGlobalCheckedIds={setGlobalCheckedIds}
          selectedPatientData={selectedPatient}
          setSelectedPatientData={setSelectedPatient}
          activeMessageModal={activeMessageModal}
          setActiveMessageModal={setActiveMessageModal}
          singleSendMessage={true}
          previousScroll={previousScrollPosition}
          setCheckedIds={setCheckedIds}
          messageTemplate={messageTemplate}
        />
      )}
    </>
  );
}
